import { useCallback, useEffect, useState } from 'react';
import {
  useBlocker,
  useNavigate,
  useOutletContext,
  useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import ModalMockFillButton from 'components/ModalMockFillButton/ModalMockFillButton';
import AppRoutes from 'constants/AppRoutes';
import { DATA_SETS_ENUM_TYPES, UID_NAME_TUPLES } from 'constants/dataSets';
import {
  NACE_CODE,
  NACE_SECTION,
  VALUE_CHAIN_LIST,
  VALUE_CHAIN_SECTION,
} from 'constants/formFields';
import { BLOCKER_STATES } from 'constants/general';
import useDataSetsDate from 'hooks/useDataSetsDate';
import useFieldsErrors from 'hooks/useFieldsErrors';
import useGeoLocations from 'hooks/useGeoLocations';
import useScrollToError from 'hooks/useScrollToError';
import useValueChainPosition from 'hooks/useValueChainPosition';
import i18n from 'i18n';
import { getCreateOwnOperationsPageMockValues } from 'mocks/dataSetsCreatePageMocks';
import {
  useEnumQuery,
  useGetNaceCodeTreeQuery,
  useOwnOperationsCreateMutation,
  useOwnOperationsRecordUpdateMutation,
  useUidNameTuplesQuery,
} from 'store/slices/dataSets/apis/dataSetsApi';
import {
  selectEnums, selectNaceCodes, selectUidNameTuples,
} from 'store/slices/dataSets/selectors';
import { useUserMeQuery } from 'store/slices/user/apis/user';
import getRequestDataForOwnOperations from 'utils/formHelpers/getRequestDataForOwnOperations';

import useCreateOwnOperationsForm from './hooks/useCreateOwnOperationsForm';
import CreateOwnOperationsView from './CreateOwnOperationsView';

import type IOption from 'components/MappingModal/interfaces/IOption';
import type IDataSetsOutletContext from 'layout/DataSetsLayout/interfaces/IDataSetsOutletContext';
import type { ReactElement } from 'react';
import type { Path, PathValue } from 'react-hook-form';
import type IOperationsRecordRequest from 'store/slices/dataSets/interfaces/IOperationsRecordRequest';
import type { TCreateOperationsSchema } from 'utils/validators';

const EMPTY_OPTIONS: IOption[] = [];

function CreateOwnOperations(): ReactElement {
  const navigate = useNavigate();
  const { recordId = '' } = useParams();
  const { setTitleAction } = useOutletContext<IDataSetsOutletContext>();

  const { data: naceCodesData, isLoading: isNaceCodesTreeLoading } = useGetNaceCodeTreeQuery(undefined, {
    selectFromResult: (result) => ({ ...result, data: selectNaceCodes(result.data) }),
  });

  const { data: userData, isLoading: isUserMeLoading } = useUserMeQuery();

  const companyCurrency = userData?.data?.company?.currency;

  const [ownOperationsCreate, { isLoading: isOwnOperationsCreateLoading }] = useOwnOperationsCreateMutation();

  const [ownOperationsRecordUpdate, {
    isLoading: isOwnOperationsRecordUpdateLoading,
  }] = useOwnOperationsRecordUpdateMutation();
  const { setFieldsErrors } = useFieldsErrors();

  const { data: enumData, isLoading: isEnumLoading } = useEnumQuery({
    types: [DATA_SETS_ENUM_TYPES.currency, DATA_SETS_ENUM_TYPES.ownership, DATA_SETS_ENUM_TYPES.valueChainSection,
      DATA_SETS_ENUM_TYPES.businessUnitType, DATA_SETS_ENUM_TYPES.assetCategory, DATA_SETS_ENUM_TYPES.ownedLeased],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectEnums(result.data) }),
  });

  const { data: profitCenters, isLoading: isProfitCenterIdsLoading } = useUidNameTuplesQuery({
    types: [UID_NAME_TUPLES.profitCenter],
  }, {
    selectFromResult: (result) => ({ ...result, data: selectUidNameTuples(result.data) }),
  });

  const profitCentersOptions = profitCenters?.[UID_NAME_TUPLES.profitCenter] ?? EMPTY_OPTIONS;

  const isPageLoading = isEnumLoading || isNaceCodesTreeLoading
      || isOwnOperationsCreateLoading || isOwnOperationsRecordUpdateLoading
      || isUserMeLoading || isProfitCenterIdsLoading;

  const ownOperationsEndpoint = recordId ? ownOperationsRecordUpdate : ownOperationsCreate;
  const {
    control,
    handleSubmit,
    isDirty,
    reset,
    setError,
    fieldsName,
    watch,
    setValue,
    formState,
  } = useCreateOwnOperationsForm({ recordId, isPageLoading, companyCurrency });

  const {
    handleGetVcOptions,
    vcOptionsList,
    listPosition,
  } = useValueChainPosition(watch(VALUE_CHAIN_LIST)?.value);

  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => isDirty
      && currentLocation.pathname !== nextLocation.pathname
      && !isConfirmationModalOpen);

  useEffect(() => {
    if (blocker.state === BLOCKER_STATES.blocked) {
      if (isDirty) {
        setIsConfirmationModalOpen(true);
      } else {
        blocker.proceed?.();
      }
    }
  }, [blocker, isDirty]);

  useScrollToError({ formState });

  const onOwnOperationsSubmit = handleSubmit((values) => {
    const requestData: IOperationsRecordRequest = getRequestDataForOwnOperations({
      values,
      recordId,
    });

    ownOperationsEndpoint(requestData).unwrap()
      .then(
        () => {
          toast.success(i18n.t(
            recordId ? 'notifications.recordHasBeenUpdated'
              : 'notifications.recordHasBeenAdded',
          ));
          reset(undefined, { keepValues: true });
          navigate(AppRoutes.balanceSheetAssets);
        },
      ).catch((errors) => {
        setFieldsErrors<TCreateOperationsSchema>({ errors, setError, fieldsName });
      });
  });

  const onHandleCloseConfirmation = (isOpen = false) => {
    if (!isConfirmationModalOpen && !isDirty) {
      navigate(-1);
    } else {
      setIsConfirmationModalOpen(isOpen);
    }
  };

  const onDiscardChangesConfirm = () => {
    const currentLocation = blocker?.location?.pathname;
    if (currentLocation) {
      navigate(currentLocation);
    } else {
      navigate(-1);
    }
    reset(undefined, { keepValues: true });
  };

  const naceDivisions = watch(NACE_SECTION)?.divisions || [];

  const onNaceSelectionChange = () => {
    const emptyOption = null as PathValue<TCreateOperationsSchema, Path<TCreateOperationsSchema>>;
    setValue(NACE_CODE, emptyOption);
  };

  const {
    onFindCountries, onFindStates, onFindCities, isStatesDisabled, onChangeCountry, onChangeState,
    isCitiesDisabled, defaultCountries,
  } = useGeoLocations<TCreateOperationsSchema>({
    watch, setValue,
  });

  const {
    dateOpened, onDateOpenedChange, onStartDateChange,
    startDate,
  } = useDataSetsDate<TCreateOperationsSchema>({ watch, setValue });

  const isMockFillVisible = !recordId && process.env.REACT_APP_API?.includes('api.dev');

  const onFillMockData = useCallback(() => {
    const valueChainListOption = watch(VALUE_CHAIN_LIST);
    const valueChainSectionOption = watch(VALUE_CHAIN_SECTION);

    reset({
      ...watch(),
      ...getCreateOwnOperationsPageMockValues({
        enumData,
        naceCodesData,
        profitCenterOptions: profitCentersOptions,
        companyCurrency,
        countryOptions: defaultCountries,
      }),
      [VALUE_CHAIN_LIST]: valueChainListOption?.value ? valueChainListOption : vcOptionsList?.[0],
      [VALUE_CHAIN_SECTION]: valueChainSectionOption?.value ? valueChainSectionOption : listPosition?.[0],
    });
  }, [
    companyCurrency,
    defaultCountries,
    enumData,
    listPosition,
    naceCodesData,
    profitCentersOptions,
    reset,
    vcOptionsList,
    watch,
  ]);

  useEffect(() => {
    if (!isMockFillVisible) {
      setTitleAction(null);
      return undefined;
    }

    setTitleAction(
      <ModalMockFillButton
        onClick={onFillMockData}
        disabled={isPageLoading}
      />,
    );

    return () => {
      setTitleAction(null);
    };
  }, [isMockFillVisible, isPageLoading, onFillMockData, setTitleAction]);

  return (
    <CreateOwnOperationsView
      control={control}
      onOwnOperationsSubmit={onOwnOperationsSubmit}
      onHandleCloseConfirmation={onHandleCloseConfirmation}
      isConfirmationModalOpen={isConfirmationModalOpen}
      onDiscardChangesConfirm={onDiscardChangesConfirm}
      enumData={enumData}
      naceCodesData={naceCodesData}
      naceDivisions={naceDivisions}
      onNaceSelectionChange={onNaceSelectionChange}
      profitCenters={profitCentersOptions}
      isPageLoading={isPageLoading}
      onFindCountries={onFindCountries}
      onFindStates={onFindStates}
      onFindCities={onFindCities}
      isStatesDisabled={isStatesDisabled}
      isCitiesDisabled={isCitiesDisabled}
      onChangeCountry={onChangeCountry}
      onChangeState={onChangeState}
      dateOpened={dateOpened}
      onDateOpenedChange={onDateOpenedChange}
      startDate={startDate}
      onStartDateChange={onStartDateChange}
      defaultCountries={defaultCountries}
      setValue={setValue}
      vcOptionsList={vcOptionsList}
      handleGetVcOptions={handleGetVcOptions}
      listPosition={listPosition}
    />
  );
}

export default CreateOwnOperations;
